/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { UserData } from '@core/users/users';
import { DONATE_LINK } from '@core/utils/env';
import { css } from '@emotion/react';
import { Button } from '@web/components/button';
import { Loading } from '@web/components/loading';
import { shareSwal } from '@web/features/layouts/share-modal';
import { TILTSwal } from '@web/services/swal';
import { UserLayout } from '@web/features/layouts/user';
import { theme } from '@web/features/theme';
import { useUserData } from '@web/features/users/use-user-data';
import { TMApi } from '@web/services/tmapi';
import { useIsRunningAsIOSApp } from '@web/utils/ios-app';
import { useAuth } from '@web/features/auth/use-auth';
import useAppBridge from '@web/components/app-bridge';
import { useIsRunningAsAndroidApp } from '@web/utils/android-app';

/**
 * # SettingsPage
 * @packageDescription
 *
 * This apge serves to allow a user to view information and to change
 *  settings associated with their account.
 *
 * This page is also the one place a user can go to manually sign out
 *  of their account.
 */
function SettingsPage() {
  const [isRunningAsIOSApp] = useIsRunningAsIOSApp();
  const [isRunningAsAndroidApp] = useIsRunningAsAndroidApp();

  const showDonateButton = !isRunningAsIOSApp;
  const [{ user }] = useAuth();
  const { requestNotificationToken } = useAppBridge();
  const isAdmin = user?.role === 'ADMIN';

  const requestNotificationPermission = () => {
    requestNotificationToken();
  };

  return (
    <UserLayout withHomeButton>
      <section css={styles.section}>
        <h2 css={styles.sectionTitle}>Email Notifications</h2>

        <div css={styles.sectionBody}>
          <ToggleEmailNotifications />
        </div>
      </section>

      <section css={styles.section}>
        <h2 css={styles.sectionTitle}>Contact Us</h2>

        <div css={styles.sectionBody}>
          <p>
            If you run into technical problems, <br /> please email{' '}
            <a
              href="mailto:help@applied.love"
              target="_new"
              css={styles.helpLink}
            >
              help@applied.love
            </a>
          </p>
        </div>
      </section>

      <section css={styles.section}>
        <h2 css={styles.sectionTitle}>Delete Account</h2>
        <p>
          If you don't want to use Time Machine anymore, you can delete your
          account. This cannot be undone.
        </p>

        <Button
          style={{
            backgroundColor: theme.colors.info,
            height: '2em',
            marginTop: '1em',
          }}
          onClick={requestAccountDeletion}
        >
          Delete Account
        </Button>
      </section>

      {isAdmin && (
        <section css={styles.section}>
          <h2 css={styles.sectionTitle}>Admin</h2>
          <AdminAccountDeletion />
        </section>
      )}

      <section css={styles.section}>
        <h2 css={styles.sectionTitle}>Meta</h2>
        <p>You are logged in as {user?.email}.</p>

        {user?.communities.length === 1 && (
          <p>
            You are member of the "{user?.communities[0].name}" community of
            care.{' '}
          </p>
        )}
        {user?.communities.length > 1 && (
          <p>
            You are member of the {user?.communities.map((comm) => comm.name)}{' '}
            communities of care.
          </p>
        )}
      </section>

      {isRunningAsAndroidApp && (
        <section style={{ marginTop: '1em' }}>
          <Button onClick={requestNotificationPermission}>
            Request Notification Permission
          </Button>
        </section>
      )}

      <section css={styles.buttonGroup}>
        <Button
          onClick={shareSwal}
          style={{ backgroundColor: theme.colors.info }}
        >
          Share Time Machine
        </Button>

        {showDonateButton && (
          <a
            href={DONATE_LINK}
            target="_new"
            rel="noopener noreferrer"
            css={styles.donateLink}
          >
            Donate to TILT
          </a>
        )}
      </section>
    </UserLayout>
  );
}

const styles = {
  section: css`
    padding-top: ${theme.space[6]};
  `,
  sectionTitle: css`
    font-weight: ${theme.fontWeights.bold};
    margin-bottom: ${theme.space[2]};
  `,
  sectionBody: css``,
  buttonGroup: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding-top: ${theme.space[48]};

    & > * {
      margin-bottom: ${theme.space['6']};
    }
  `,
  donateLink: css``,
  adminEmailInput: css`
    width: 100%;
    max-width: ${theme.space.sm};
    padding: ${theme.space[2]};
  `,
  helpLink: css`
    font-weight: ${theme.fontWeights.bold};
  `,

  toggleContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: ${theme.space.sm};
  `,
  toggleLabel: css`
    position: relative;
    display: inline-block;
    width: 30px;
    height: 12px;
  `,
  toggleInput: css`
    opacity: 0;
    width: 0;
    height: 0;
    background-color: ${theme.colors.info};

    &:checked + span {
      background-color: ${theme.colors.info};
    }

    &:focus + span {
      box-shadow: 0 0 1px ${theme.colors.info};
    }

    &:checked + span:before {
      transform: translateX(100%);
    }
  `,
  toggleSlider: css`
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${theme.colors.whiteAlpha[700]};
    border-radius: ${theme.radii.full};
    transition: 0.4s;

    &:before {
      content: '';
      position: absolute;
      height: 12px;
      width: 15px;
      left: 0px;
      bottom: 0px;
      background-color: ${theme.colors.whiteAlpha[900]};
      border-radius: ${theme.radii.full};
      transition: 0.4s;
    }
  `,
};

function requestAccountDeletion() {
  const tmapi = new TMApi();
  tmapi.requestAccountDeletion().then(() => {
    TILTSwal.fire({
      title: 'Deletion requested',
      text: "We've sent you an email to confirm your account deletion. If you changed your mind and want to keep your account, ignore the email.",
    });
  });
}

function AdminAccountDeletion() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!email) {
      return;
    }

    const { isConfirmed } = await TILTSwal.fire({
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Send deletion email',
      cancelButtonText: 'Cancel',
      text: `Send an account deletion confirmation email to ${email}? The account is only deleted once they confirm via the link in that email.`,
    });
    if (!isConfirmed) {
      return;
    }

    setSubmitting(true);
    try {
      await new TMApi().adminRequestAccountDeletion(email);
      setEmail('');
      await TILTSwal.fire({
        icon: 'success',
        text: `Deletion email sent to ${email}.`,
      });
    } catch (error) {
      await TILTSwal.fire({
        icon: 'error',
        text: (error as Error).message || 'Something went wrong.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div css={styles.sectionBody}>
      <p>Send an account deletion request on behalf of a user by email.</p>

      <input
        type="email"
        placeholder="user@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        css={styles.adminEmailInput}
      />

      <Button
        style={{
          backgroundColor: theme.colors.info,
          height: '2em',
          marginTop: '1em',
        }}
        disabled={submitting || !email}
        onClick={submit}
      >
        Request Account Deletion
      </Button>
    </div>
  );
}

export default SettingsPage;
function ToggleEmailNotifications() {
  const [{ userData }, { toggleFunctionalityNotifications }] = useUserData();

  const handleChange = (userDataKey: keyof UserData) => {
    return () =>
      toggleFunctionalityNotifications(
        userDataKey as unknown as Parameters<
          typeof toggleFunctionalityNotifications
        >[0], // throws error if not valid
      ).catch(() => null);
  };

  if (!userData) {
    return <Loading />;
  }

  const options: Array<{ userDataKey: keyof UserData; label: string }> = [
    {
      userDataKey: 'f1_notifications_enabled',
      label: 'Time Machine',
    },
    {
      userDataKey: 'f2_notifications_enabled',
      label: 'Meditation Tent',
    },
    {
      userDataKey: 'f3_notifications_enabled',
      label: 'HopeGarden',
    },
  ];

  return (
    <form>
      {options.map(({ userDataKey, label }) => (
        <fieldset css={styles.toggleContainer}>
          <p>{label}</p>

          <label
            key={userDataKey}
            htmlFor={userDataKey}
            css={styles.toggleLabel}
          >
            <input
              className="rr-ignore checkbox"
              type="checkbox"
              name={userDataKey}
              id={userDataKey}
              checked={
                userData[userDataKey as keyof typeof userData] as boolean
              }
              onChange={handleChange(userDataKey)}
              css={styles.toggleInput}
            />
            <span css={styles.toggleSlider} />
          </label>
        </fieldset>
      ))}
    </form>
  );
}
